import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Loader2, FolderOpen } from 'lucide-react';
import { ResponseQuestionRow, qualityOf } from './ResponseQuestionRow';
import { LibraryWordingDialog } from './LibraryWordingDialog';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const MyQuestionsView = ({ assignees, canManage, onOpenProject }) => {
  const [rows, setRows] = useState(null);
  const [fSearch, setFSearch] = useState('');
  const [fMatch, setFMatch] = useState('all');
  const [fConf, setFConf] = useState('all');
  const [fFit, setFFit] = useState('all');

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/rfp/response/my-questions`, { withCredentials: true });
      setRows(res.data?.questions || []);
    } catch { setRows([]); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const update = async (row, fields) => {
    try {
      const res = await axios.put(
        `${API_URL}/api/rfp/response/projects/${row.project_id}/documents/${row.doc_id}/questions/${row.idx}`,
        fields, { withCredentials: true });
      const { _doc_scores: _ignored, ...updatedQ } = res.data || {};
      setRows(prev => prev.map(r => (r.project_id === row.project_id && r.doc_id === row.doc_id && r.idx === row.idx
        ? { ...r, ...updatedQ } : r)));
    } catch (err) { toast.error(err?.response?.data?.detail || 'Could not update question'); }
  };

  const [wordingReview, setWordingReview] = useState(null);

  const toLibrary = async (row, wording = {}) => {
    try {
      const res = await axios.post(
        `${API_URL}/api/rfp/response/projects/${row.project_id}/documents/${row.doc_id}/questions/${row.idx}/to-library`,
        wording, { withCredentials: true });
      if (res.data.needs_review) { setWordingReview({ ...res.data, row }); return; }
      setWordingReview(null);
      toast.success(`Added to the library as ${res.data.capability_id}`);
      setRows(prev => prev.map(r => (r.project_id === row.project_id && r.doc_id === row.doc_id && r.idx === row.idx
        ? { ...r, library_capability_id: res.data.capability_id } : r)));
    } catch (err) { toast.error(err?.response?.data?.detail || 'Could not add to library'); }
  };

  if (rows === null) return <Loader2 className="w-5 h-5 animate-spin text-[#4ABFCF]" />;
  if (rows.length === 0) {
    return <p className="text-sm text-slate-400 py-8 text-center" data-testid="rfp-resp-myq-empty">No RFP questions are assigned to you right now.</p>;
  }
  const DONE = ['accepted', 'accepted_modified', 'accepted_manual'];
  const s = fSearch.trim().toLowerCase();
  const filtered = rows.filter(r =>
    (fMatch === 'all' || (fMatch === 'pricing' ? !!r.is_pricing : r.strength === fMatch && !r.is_pricing)) &&
    (fConf === 'all' || r.confidence === fConf) &&
    (fFit === 'all' || r.fit === fFit) &&
    (!s || `${r.text} ${r.response || ''}`.toLowerCase().includes(s)));
  const sorted = [...filtered].sort((a, b) =>
    (DONE.includes(a.flag) - DONE.includes(b.flag)) ||
    ((a.project_open === false) - (b.project_open === false)) ||
    (qualityOf(a) - qualityOf(b)));
  return (
    <div className="space-y-3" data-testid="rfp-resp-myquestions">
      <div className="flex flex-wrap items-center gap-2" data-testid="rfp-myq-filter-bar">
        <input type="text" value={fSearch} onChange={e => setFSearch(e.target.value)}
          placeholder="Search question or answer…"
          className="h-7 text-[11px] border border-slate-200 rounded-md px-2 bg-white text-slate-600 w-[190px] focus:outline-none focus:border-[#4ABFCF]"
          data-testid="rfp-myq-search" />
        <select value={fMatch} onChange={e => setFMatch(e.target.value)}
          className="h-7 text-[11px] border border-slate-200 rounded-md px-1.5 bg-white text-slate-600"
          data-testid="rfp-myq-match">
          <option value="all">All matches</option>
          <option value="strong">Strong</option>
          <option value="partial">Partial</option>
          <option value="none">Gap</option>
          <option value="pricing">Pricing</option>
        </select>
        <select value={fConf} onChange={e => setFConf(e.target.value)}
          className="h-7 text-[11px] border border-slate-200 rounded-md px-1.5 bg-white text-slate-600"
          data-testid="rfp-myq-conf">
          <option value="all">Any confidence</option>
          <option value="high">Confidence: high</option>
          <option value="med">Confidence: med</option>
          <option value="low">Confidence: low</option>
        </select>
        <select value={fFit} onChange={e => setFFit(e.target.value)}
          className="h-7 text-[11px] border border-slate-200 rounded-md px-1.5 bg-white text-slate-600"
          data-testid="rfp-myq-fit">
          <option value="all">Any fit</option>
          <option value="high">Fit: high</option>
          <option value="med">Fit: med</option>
          <option value="low">Fit: low</option>
        </select>
        <span className="text-[10px] text-slate-400" data-testid="rfp-myq-count">
          showing {sorted.length} of {rows.length}
        </span>
      </div>
      <p className="text-[10px] text-slate-400" data-testid="rfp-resp-myq-sort-note">
        Sorted weakest-first across your open RFPs — shore up the shakiest answers before their deadlines.
      </p>
      {sorted.length === 0 && (
        <p className="text-sm text-slate-400 py-6 text-center" data-testid="rfp-myq-no-match">
          No assigned questions match these filters.
        </p>
      )}
      {sorted.map(row => (
        <div key={`${row.project_id}-${row.doc_id}-${row.idx}`} className="border border-slate-200 rounded-md">
          <div className="flex items-center gap-2 px-3 py-1.5 bg-slate-50 border-b border-slate-100 rounded-t-md">
            <span className="text-[11px] font-semibold text-[#0F2040] truncate">{row.project_name}</span>
            <span className="text-[10px] text-slate-400 truncate">· {row.doc_name}</span>
            <span className="flex-1" />
            <Button size="sm" variant="ghost" className="h-6 text-[10px] px-2" onClick={() => onOpenProject(row.project_id)}
              data-testid={`rfp-resp-myq-open-${row.project_id}-${row.idx}`}>
              <FolderOpen className="w-3 h-3 mr-1" />Open project
            </Button>
          </div>
          <ResponseQuestionRow q={row} docId={row.doc_id} assignees={assignees} canManage={canManage}
            scaleLevels={row.scale_levels} projectId={row.project_id}
            onUpdate={(d, i, fields) => update(row, fields)} onToLibrary={() => toLibrary(row)} />
        </div>
      ))}
      <LibraryWordingDialog review={wordingReview} onCancel={() => setWordingReview(null)}
        onConfirm={(body, force) => toLibrary(wordingReview.row, { body, force })} />
    </div>
  );
};
